import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import { createElement, Fragment } from "@wordpress/element";

/**
 * External dependencies
 */
import classnames from 'classnames';
import { isEmpty, kebabCase } from 'lodash';
/**
 * WordPress dependencies
 */

import { __, sprintf } from '@wordpress/i18n';
import { useEffect, useState } from '@wordpress/element';
import { edit, close, chevronDown, chevronUp, plus } from '@wordpress/icons';
/**
 * Internal dependencies
 */

import Dropdown from '../dropdown';
import CircularOptionPicker from '../circular-option-picker';
import ColorPicker from '../color-picker';
import Button from '../button';
import TextControl from '../text-control';
import BaseControl from '../base-control';

function DropdownOpenOnMount(_ref) {
  var shouldOpen = _ref.shouldOpen,
      isOpen = _ref.isOpen,
      onToggle = _ref.onToggle;
  useEffect(function () {
    if (shouldOpen && !isOpen) {
      onToggle();
    }
  }, []);
  return null;
}

function ColorOption(_ref2) {
  var color = _ref2.color,
      name = _ref2.name,
      slug = _ref2.slug,
      _onChange = _ref2.onChange,
      onRemove = _ref2.onRemove,
      onConfirm = _ref2.onConfirm,
      _ref2$confirmLabel = _ref2.confirmLabel,
      confirmLabel = _ref2$confirmLabel === void 0 ? __('OK') : _ref2$confirmLabel,
      _ref2$isEditingNameOn = _ref2.isEditingNameOnMount,
      isEditingNameOnMount = _ref2$isEditingNameOn === void 0 ? false : _ref2$isEditingNameOn,
      _ref2$isEditingColorO = _ref2.isEditingColorOnMount,
      isEditingColorOnMount = _ref2$isEditingColorO === void 0 ? false : _ref2$isEditingColorO,
      onCancel = _ref2.onCancel,
      _ref2$immutableColorS = _ref2.immutableColorSlugs,
      immutableColorSlugs = _ref2$immutableColorS === void 0 ? [] : _ref2$immutableColorS;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isHover = _useState2[0],
      setIsHover = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isFocused = _useState4[0],
      setIsFocused = _useState4[1];

  var _useState5 = useState(isEditingNameOnMount),
      _useState6 = _slicedToArray(_useState5, 2),
      isEditingName = _useState6[0],
      setIsEditingName = _useState6[1];

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      isShowingAdvancedPanel = _useState8[0],
      setIsShowingAdvancedPanel = _useState8[1];

  var isShowingControls = (isHover || isFocused || isEditingName || isShowingAdvancedPanel) && !immutableColorSlugs.includes(slug);
  return createElement("div", {
    tabIndex: 0,
    className: classnames('components-color-edit__color-option', {
      'is-hover': isHover && !isEditingName && !isShowingAdvancedPanel
    }),
    onMouseEnter: function onMouseEnter() {
      return setIsHover(true);
    },
    onMouseLeave: function onMouseLeave() {
      return setIsHover(false);
    },
    onFocus: function onFocus() {
      return setIsFocused(true);
    },
    onBlur: function onBlur() {
      return setIsFocused(false);
    },
    "aria-label": name ? // translators: %s: The name of the color e.g: "vivid red".
    sprintf(__('Color: %s'), name) : // translators: %s: color hex code e.g: "#f00".
    sprintf(__('Color code: %s'), color)
  }, createElement("div", {
    className: "components-color-edit__color-option-main-area"
  }, createElement(Dropdown, {
    renderToggle: function renderToggle(_ref3) {
      var isOpen = _ref3.isOpen,
          onToggle = _ref3.onToggle;
      return createElement(Fragment, null, createElement(DropdownOpenOnMount, {
        shouldOpen: isEditingColorOnMount,
        isOpen: isOpen,
        onToggle: onToggle
      }), createElement(CircularOptionPicker.Option, {
        style: {
          backgroundColor: color,
          color: color
        },
        "aria-expanded": isOpen,
        "aria-haspopup": "true",
        onClick: onToggle,
        "aria-label": __('Edit color value')
      }));
    },
    renderContent: function renderContent() {
      return createElement(ColorPicker, {
        color: color,
        onChangeComplete: function onChangeComplete(newColor) {
          return _onChange({
            color: newColor.hex,
            slug: slug,
            name: name
          });
        },
        disableAlpha: true
      });
    }
  }), !isEditingName && createElement("div", {
    className: "components-color-edit__color-option-color-name"
  }, name), isEditingName && createElement(Fragment, null, createElement(TextControl, {
    className: "components-color-edit__color-option-color-name-input",
    hideLabelFromVision: true,
    onChange: function onChange(newColorName) {
      return _onChange({
        color: color,
        slug: kebabCase(newColorName),
        name: newColorName
      });
    },
    label: __('Color name'),
    placeholder: __('Name'),
    value: name
  }), createElement(Button, {
    onClick: function onClick() {
      setIsEditingName(false);
      setIsFocused(false);

      if (onConfirm) {
        onConfirm();
      }
    },
    isPrimary: true
  }, confirmLabel)), !isEditingName && createElement(Fragment, null, createElement(Button, {
    className: classnames({
      'components-color-edit__hidden-control': !isShowingControls
    }),
    icon: edit,
    label: __('Edit color name'),
    onClick: function onClick() {
      return setIsEditingName(true);
    }
  }), createElement(Button, {
    className: classnames({
      'components-color-edit__hidden-control': !isShowingControls
    }),
    icon: close,
    label: __('Remove color'),
    onClick: onRemove
  })), createElement(Button, {
    className: classnames({
      'components-color-edit__hidden-control': !isShowingControls
    }),
    icon: isShowingAdvancedPanel ? chevronUp : chevronDown,
    label: __('Additional color settings'),
    onClick: function onClick() {
      if (isShowingAdvancedPanel) {
        setIsFocused(false);
      }

      setIsShowingAdvancedPanel(!isShowingAdvancedPanel);
    },
    "aria-expanded": isShowingAdvancedPanel
  })), onCancel && createElement(Button, {
    className: "components-color-edit__cancel-button",
    onClick: onCancel
  }, __('Cancel')), isShowingAdvancedPanel && createElement(TextControl, {
    className: "components-color-edit__slug-input",
    onChange: function onChange(newSlug) {
      return _onChange({
        color: color,
        slug: newSlug,
        name: name
      });
    },
    label: __('Slug'),
    value: slug
  }));
}

function ColorInserter(_ref4) {
  var onInsert = _ref4.onInsert,
      onCancel = _ref4.onCancel;

  var _useState9 = useState({
    color: '#fff',
    name: '',
    slug: ''
  }),
      _useState10 = _slicedToArray(_useState9, 2),
      color = _useState10[0],
      setColor = _useState10[1];

  return createElement(ColorOption, {
    color: color.color,
    name: color.name,
    slug: color.slug,
    onChange: setColor,
    confirmLabel: __('Save'),
    onConfirm: function onConfirm() {
      return onInsert(color);
    },
    isEditingNameOnMount: true,
    isEditingColorOnMount: true,
    onCancel: onCancel
  });
}

export default function ColorEdit(_ref5) {
  var colors = _ref5.colors,
      _onChange2 = _ref5.onChange,
      emptyUI = _ref5.emptyUI,
      immutableColorSlugs = _ref5.immutableColorSlugs,
      _ref5$canReset = _ref5.canReset,
      canReset = _ref5$canReset === void 0 ? true : _ref5$canReset;

  var _useState11 = useState(false),
      _useState12 = _slicedToArray(_useState11, 2),
      isInsertingColor = _useState12[0],
      setIsInsertingColor = _useState12[1];

  return createElement(BaseControl, null, createElement("fieldset", null, createElement("div", {
    className: "components-color-edit__label-and-insert-container"
  }, createElement("legend", null, createElement("div", null, createElement(BaseControl.VisualLabel, null, __('Color palette')))), !isInsertingColor && createElement(Button, {
    onClick: function onClick() {
      setIsInsertingColor(true);
    },
    className: "components-color-edit__insert-button",
    icon: plus
  })), createElement("div", null, !isEmpty(colors) && colors.map(function (color, index) {
    return createElement(ColorOption, {
      key: index,
      color: color.color,
      name: color.name,
      slug: color.slug,
      immutableColorSlugs: immutableColorSlugs,
      onChange: function onChange(newColor) {
        _onChange2(colors.map(function (currentColor, currentIndex) {
          if (currentIndex === index) {
            return newColor;
          }

          return currentColor;
        }));
      },
      onRemove: function onRemove() {
        _onChange2(colors.filter(function (_currentColor, currentIndex) {
          if (currentIndex === index) {
            return false;
          }

          return true;
        }));
      }
    });
  }), isInsertingColor && createElement(ColorInserter, {
    onInsert: function onInsert(newColor) {
      setIsInsertingColor(false);

      _onChange2([].concat(_toConsumableArray(colors || []), [newColor]));
    },
    onCancel: function onCancel() {
      return setIsInsertingColor(false);
    }
  }), !isInsertingColor && isEmpty(colors) && emptyUI), !!canReset && createElement(Button, {
    isSmall: true,
    isSecondary: true,
    className: "components-color-edit__reset-button",
    onClick: function onClick() {
      return _onChange2();
    }
  }, __('Reset'))));
}
//# sourceMappingURL=index.js.map